import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, X, MessageCircle, Users, Smile } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WebSocketChat = ({ roomId, roomName, isOpen, onClose }) => {
  const { user, isAuthenticated } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [connected, setConnected] = useState(false);
  const [userCount, setUserCount] = useState(0);
  const [typingUsers, setTypingUsers] = useState([]);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (!roomId || !isAuthenticated) return;

    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const params = new URLSearchParams({
      user_id: user?.id || 'anonymous',
      user_name: user?.name || 'Anonymous',
      user_avatar: user?.avatar || ''
    });

    const ws = new WebSocket(`${wsUrl}/ws/chat/${roomId}?${params}`);

    ws.onopen = () => {
      setConnected(true);
      console.log('WebSocket connected');
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case 'connected':
          setUserCount(data.user_count);
          break;
        case 'message':
          setMessages(prev => [...prev, data.message]);
          break;
        case 'user_joined':
        case 'user_left':
          setUserCount(data.user_count);
          break;
        case 'typing':
          if (data.is_typing && data.user_name !== user?.name) {
            setTypingUsers(prev => [...new Set([...prev, data.user_name])]);
          } else {
            setTypingUsers(prev => prev.filter(u => u !== data.user_name));
          }
          break;
        default:
          break;
      }
    };

    ws.onclose = () => {
      setConnected(false);
      console.log('WebSocket disconnected');
      // Reconnect after 3 seconds
      setTimeout(() => {
        if (isOpen) connectWebSocket();
      }, 3000);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    wsRef.current = ws;
  }, [roomId, isAuthenticated, user, isOpen]);

  useEffect(() => {
    if (isOpen && roomId && isAuthenticated) {
      // Load existing messages
      fetch(`${BACKEND_URL}/api/chat/rooms/${roomId}/messages?limit=50`)
        .then(res => res.json())
        .then(data => setMessages(data))
        .catch(console.error);
      
      connectWebSocket();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [isOpen, roomId, isAuthenticated, connectWebSocket]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !wsRef.current || !connected) return;

    wsRef.current.send(JSON.stringify({
      type: 'message',
      content: newMessage.trim()
    }));

    setNewMessage('');
  };

  const handleTyping = (isTyping) => {
    if (!wsRef.current || !connected) return;

    wsRef.current.send(JSON.stringify({
      type: 'typing',
      is_typing: isTyping
    }));

    // Clear typing indicator after 2 seconds
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    if (isTyping) {
      typingTimeoutRef.current = setTimeout(() => {
        handleTyping(false);
      }, 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-4 bottom-4 w-96 h-[500px] bg-gray-900 rounded-xl border border-gray-800 shadow-2xl flex flex-col z-50 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageCircle size={20} className="text-white" />
          <span className="text-white font-semibold">{roomName || 'Live Chat'}</span>
          {connected && (
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-white/70 text-sm">
            <Users size={16} />
            <span>{userCount}</span>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <MessageCircle size={40} className="mx-auto mb-2 opacity-50" />
            <p>No messages yet</p>
            <p className="text-sm">Be the first to say hello!</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex gap-2 ${msg.user_id === user?.id ? 'flex-row-reverse' : ''}`}
            >
              <div className="flex-shrink-0">
                {msg.user_avatar ? (
                  <img src={msg.user_avatar} alt={msg.user_name} className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                    <span className="text-white text-xs font-medium">
                      {msg.user_name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                )}
              </div>
              
              <div className={`max-w-[70%] ${msg.user_id === user?.id ? 'text-right' : ''}`}>
                <p className="text-gray-500 text-xs mb-1">{msg.user_name}</p>
                <div
                  className={`rounded-lg px-3 py-2 ${
                    msg.user_id === user?.id
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-800 text-gray-200'
                  }`}
                >
                  {msg.content}
                </div>
                <p className="text-gray-600 text-xs mt-1">
                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))
        )}
        
        {/* Typing indicator */}
        {typingUsers.length > 0 && (
          <div className="text-gray-500 text-sm italic">
            {typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      {isAuthenticated ? (
        <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-800">
          <div className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                handleTyping(e.target.value.length > 0);
              }}
              placeholder={connected ? "Type a message..." : "Connecting..."}
              disabled={!connected}
              maxLength={500}
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-red-500 focus:outline-none disabled:opacity-50"
              data-testid="ws-chat-input"
            />
            <button
              type="submit"
              disabled={!connected || !newMessage.trim()}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors"
              data-testid="ws-chat-send-btn"
            >
              <Send size={20} />
            </button>
          </div>
        </form>
      ) : (
        <div className="p-4 border-t border-gray-800 text-center">
          <p className="text-gray-400 text-sm">
            Please <span className="text-red-500 cursor-pointer">sign in</span> to chat
          </p>
        </div>
      )}
    </div>
  );
};

export default WebSocketChat;
